'use client';

import { Box, Container, Typography } from '@mui/material';
import Link from 'next/link';
import CookieActions from './components/CookieActions';
import CookiePreference from './components/CookiePreference';

export default function CookiesZhTW({ settings, handleCookieChange, handleSaveSettings, handleRejectAll, notification }: I18nCookiesProps) {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Cookie 設置
        </Typography>
      </Box>

      {/* 簡介 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          我們在RightPulse上使用Cookie來提升您的體驗、分析流量並個性化內容和廣告。通過點擊“接受所有Cookie”，您即同意我們按照隱私政策和Cookie政策中使用Cookie的方式。不過，您可以在下方管理您的Cookie設置。
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          管理您的Cookie偏好
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          我們在平台上使用不同類型的Cookie。請選擇您希望允許的Cookie類型：
        </Typography>
      </Box>

      {/* Cookie 類型 */}
      <Box sx={{ mb: 4, pl: 4 }}>
        {/* 嚴格必要的Cookie */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            1. 嚴格必要的Cookie
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            這些Cookie對於網站的正常運行至關重要，無法關閉。它們通常在您執行某些操作（如登錄或填寫表單）時設置。
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            始終啟用 —— 您無法禁用這些Cookie。
          </Typography>
        </Box>

        {/* 性能和分析Cookie */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            2. 性能和分析Cookie
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            這些Cookie幫助我們了解訪客如何與我們的網站互動，例如您最常訪問的頁面。我們使用這些信息來改進我們的網站和服務。
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>Google Analytics</li>
            <li>Mixpanel</li>
            <li>其他分析工具</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            您可以隨時選擇禁用或啟用這些Cookie。
          </Typography>
          <CookiePreference 
            label="啟用性能Cookie"
            type="performance"
            initialValue={settings.performance}
            onChange={handleCookieChange}
          />
        </Box>

        {/* 功能性Cookie */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            3. 功能性Cookie
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            這些Cookie允許我們記住您的偏好（如語言或地區），並提供更增強、更個性化的功能。
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>語言偏好</li>
            <li>登錄詳情</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            您可以隨時選擇禁用或啟用這些Cookie。
          </Typography>
          <CookiePreference 
            label="啟用功能性Cookie"
            type="functional"
            initialValue={settings.functional}
            onChange={handleCookieChange}
          />
        </Box>

        {/* 廣告和定向Cookie */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            4. 廣告和定向Cookie
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            這些Cookie用於根據您的瀏覽習慣投放個性化廣告。它們還用於限制您看到同一廣告的次數，並幫助我們衡量營銷活動的效果。
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>Google AdSense</li>
            <li>Facebook Pixel</li>
            <li>其他廣告網絡</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            您可以隨時選擇禁用或啟用這些Cookie。
          </Typography>
          <CookiePreference 
            label="啟用廣告Cookie"
            type="advertising"
            initialValue={settings.advertising}
            onChange={handleCookieChange}
          />
        </Box>
      </Box>

      {/* 其他信息 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          如何管理您的Cookie設置
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          您可以隨時訪問我們的Cookie設置頁面更新您的偏好。如果您禁用了某些Cookie，網站的部分功能可能無法正常運行。
        </Typography>
      </Box>

      {/* 同意聲明 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          同意
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          如果您希望限制我們使用的Cookie類型，請在上方的選項中進行選擇並點擊“確認”。
        </Typography>
      </Box>

      {/* 瞭解更多 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          瞭解更多
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          如果您想瞭解更多關於我們如何保護您的隱私的信息，請查看我們的{' '}
          <Link 
            href="/legal/privacy" 
            style={{ 
              color: 'primary.main', 
              textDecoration: 'underline',
            }}
          >
            隱私政策
          </Link>
          。
        </Typography>
      </Box>

      {/* 操作按鈕 */}
      <CookieActions 
        onReject={handleRejectAll}
        onSave={handleSaveSettings}
      />

      {/* 通知 */}
      {notification}
    </Container>
  );
}
